/**
 * Notifications Page
 * Full-page notification management interface
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Alert,
  Snackbar,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Delete as DeleteIcon,
  DoneAll as MarkAllReadIcon,
  DeleteSweep as ClearAllIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Circle as UnreadIcon,
  FolderOpen as EmptyIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  Notification,
  NotificationType,
  NotificationPriority,
  NotificationFilter,
} from '../../types/notification';
import { notificationService } from '../../services/notificationService';
import { logger } from '../../services/loggerService';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const navigate = useNavigate();

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationService.getNotifications();
      setNotifications(data);
    } catch (error) {
      logger.error('Error loading notifications', error, { component: 'Notifications' });
      setSnackbar({ open: true, message: 'Failed to load notifications', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    // Subscribe to real-time updates
    const unsubscribe = notificationService.subscribeToNotifications((updatedNotifications) => {
      setNotifications(updatedNotifications);
    });

    return () => unsubscribe();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
    setAnchorEl(event.currentTarget);
    setCurrentNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentNotification(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.isRead) {
        await notificationService.markAsRead(notification.id);
      }

      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    } catch (error) {
      logger.error('Error handling notification click', error);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const filtered = getFilteredNotifications();
      setSelected(filtered.map((n) => n.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleMarkSelectedAsRead = async () => {
    try {
      await notificationService.markMultipleAsRead(selected);
      setSelected([]);
      setSnackbar({ open: true, message: 'Marked as read', severity: 'success' });
    } catch (error) {
      logger.error('Error marking notifications as read', error);
      setSnackbar({ open: true, message: 'Error marking as read', severity: 'error' });
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await notificationService.deleteMultiple(selected);
      setSelected([]);
      setSnackbar({ open: true, message: 'Notifications deleted', severity: 'success' });
    } catch (error) {
      logger.error('Error deleting notifications', error);
      setSnackbar({ open: true, message: 'Error deleting notifications', severity: 'error' });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setSnackbar({ open: true, message: 'All notifications marked as read', severity: 'success' });
    } catch (error) {
      logger.error('Error marking all as read', error);
      setSnackbar({ open: true, message: 'Error marking all as read', severity: 'error' });
    }
  };

  const handleClearAllRead = async () => {
    try {
      await notificationService.deleteAllRead();
      setSnackbar({ open: true, message: 'Read notifications cleared', severity: 'success' });
    } catch (error) {
      logger.error('Error clearing read notifications', error);
      setSnackbar({ open: true, message: 'Error clearing notifications', severity: 'error' });
    }
  };

  const handleDeleteOne = async () => {
    if (!currentNotification) return;
    
    try {
      await notificationService.deleteNotification(currentNotification.id);
      setSnackbar({ open: true, message: 'Notification deleted', severity: 'success' });
      handleMenuClose();
    } catch (error) {
      logger.error('Error deleting notification', error);
      setSnackbar({ open: true, message: 'Error deleting notification', severity: 'error' });
    }
  };

  const handleMarkOneAsRead = async () => {
    if (!currentNotification) return;
    
    try {
      await notificationService.markAsRead(currentNotification.id);
      setSnackbar({ open: true, message: 'Marked as read', severity: 'success' });
      handleMenuClose();
    } catch (error) {
      logger.error('Error marking as read', error);
      setSnackbar({ open: true, message: 'Error marking as read', severity: 'error' });
    }
  };

  const getFilteredNotifications = () => {
    switch (tabValue) {
      case 0: // All
        return notifications;
      case 1: // Unread
        return notifications.filter((n) => !n.isRead);
      case 2: // Orders
        return notifications.filter((n) => 
          n.type === NotificationType.ORDER_NEW || 
          n.type === NotificationType.ORDER_UPDATE || 
          n.type === NotificationType.ORDER_CANCELLED
        );
      case 3: // Products
        return notifications.filter((n) => 
          n.type === NotificationType.PRODUCT_LOW_STOCK || 
          n.type === NotificationType.PRODUCT_OUT_OF_STOCK
        );
      default:
        return notifications;
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case NotificationPriority.URGENT:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.MEDIUM:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = getFilteredNotifications();
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body1" color="text.secondary">
            Manage your notifications and stay updated on important events
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} unread`}
              color="error"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadNotifications}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<MarkAllReadIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none' }}
            >
              Mark All Read
            </Button>
          )}
          {notifications.some((n) => n.isRead) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={handleClearAllRead}
              sx={{ textTransform: 'none' }}
            >
              Clear Read
            </Button>
          )}
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${notifications.length})`} />
          <Tab label={`Unread (${unreadCount})`} />
          <Tab label="Orders" />
          <Tab label="Products" />
        </Tabs>
      </Card>

      {/* Selection Actions */}
      {selected.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={handleMarkSelectedAsRead}
                sx={{ textTransform: 'none' }}
              >
                Mark Read
              </Button>
              <Button
                size="small"
                color="error"
                onClick={handleDeleteSelected}
                sx={{ textTransform: 'none' }}
              >
                Delete
              </Button>
            </Box>
          }
        >
          {selected.length} notification{selected.length > 1 ? 's' : ''} selected
        </Alert>
      )}

      {/* Notifications List */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Select All */}
          {filteredNotifications.length > 0 && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Checkbox
                checked={selected.length === filteredNotifications.length && filteredNotifications.length > 0}
                indeterminate={selected.length > 0 && selected.length < filteredNotifications.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                Select All
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {filteredNotifications.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <EmptyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tabValue === 1 ? "You're all caught up!" : "No notifications to display"}
              </Typography>
            </Box>
          )}

          {/* Notifications */}
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={(e) => handleMenuOpen(e, notification)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <Checkbox
                  checked={selected.includes(notification.id)}
                  onChange={() => handleSelectOne(notification.id)}
                />
                <ListItemButton
                  onClick={() => handleNotificationClick(notification)}
                  sx={{ flex: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {!notification.isRead && (
                          <UnreadIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                        )}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.isRead ? 400 : 600,
                            flex: 1,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.priority}
                          size="small"
                          color={getPriorityColor(notification.priority) as any}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={notification.message}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {currentNotification && !currentNotification.isRead && (
          <MenuItem onClick={handleMarkOneAsRead}>
            <ListItemIcon>
              <MarkAllReadIcon fontSize="small" />
            </ListItemIcon>
            Mark as Read
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteOne} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
